import { useEffect, useRef, useState } from 'react';

import { useEditorStore } from '../layout-builder/store/editorStore';
import { ModelPicker } from './ModelPicker';
import { useAISession, type ChatMessage, type ToolCall } from './useAISession';

/**
 * AI Chat panel for the View Builder. Mounts as a section in the
 * right-column VerticalSplit, alongside PropertyPanel / ThemeEditor /
 * ContentEditor / BuildDashboard.
 *
 * Visible state:
 *   - chat history (scrollable, auto-scrolls to bottom on new content)
 *   - composer with Enter-to-send
 *   - Stop button while streaming
 *   - "AI is editing…" banner during a turn
 *   - inline tool-call cards
 *   - confirmation dialog when the server emits needs_confirm
 *
 * Selection awareness: the currently-selected view id (from
 * useEditorStore) is sent with each message so the LLM has it as
 * context.
 */
export interface AIChatPanelProps {
  appId: string | undefined;
}

export function AIChatPanel({ appId }: AIChatPanelProps) {
  const selectedViewId = useEditorStore((s) => s.selectedViewId);
  const session = useAISession(appId);
  const [draft, setDraft] = useState('');
  const scrollRef = useRef<HTMLDivElement | null>(null);

  // Auto-scroll the message list when content lands.
  useEffect(() => {
    const el = scrollRef.current;
    if (el) el.scrollTop = el.scrollHeight;
  }, [session.messages, session.isStreaming]);

  const onSubmit = () => {
    const text = draft.trim();
    if (!text || session.isStreaming) return;
    setDraft('');
    session.send(text, selectedViewId ?? undefined);
  };

  return (
    <section style={s.section}>
      <header style={s.header}>
        <span style={s.headingText}>AI Assistant</span>
        {session.isStreaming && <span style={s.streamingTag}>editing…</span>}
        {session.sessionStarted && session.sessionModel && (
          <span
            style={s.activeModelTag}
            title="Model is locked for this chat session. Start a new chat to change it."
          >
            {session.sessionModel}
            {session.sessionModelSource === 'override' && ' · override'}
          </span>
        )}
      </header>

      {/* Model picker — pre-session: editable; post-session: locked.
          Server enforces sticky-per-session, so we mirror that in the
          UI to avoid a confusing "I picked Sonnet but it stayed on
          Opus" experience. Users can still see what's active. */}
      <ModelPicker
        flow="chat"
        value={session.selectedModel}
        onChange={session.setSelectedModel}
        variant="inline"
        disabled={session.sessionStarted}
        disabledHint={session.sessionStarted ? 'Locked for this session' : undefined}
      />

      <div ref={scrollRef} style={s.messages}>
        {session.messages.length === 0 && (
          <p style={s.empty}>
            Ask the AI to add screens, change styles, or wire interactions. It will
            inspect the current app and make the edits live. Selected view is sent
            as context.
          </p>
        )}
        {session.messages.map((m) => (
          <MessageRow key={m.id} message={m} />
        ))}
      </div>

      {session.error && <p style={s.error}>{session.error}</p>}

      <ConfirmDialog
        confirm={session.pendingConfirm}
        onAnswer={session.respondToConfirm}
      />

      <div style={s.composer}>
        <textarea
          style={s.textarea}
          value={draft}
          placeholder={selectedViewId
            ? `Ask the AI… (selected view ${selectedViewId.slice(0, 8)})`
            : 'Ask the AI…'}
          rows={3}
          onChange={(e) => setDraft(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === 'Enter' && !e.shiftKey) {
              e.preventDefault();
              onSubmit();
            }
          }}
          disabled={session.isStreaming || !appId}
        />
        <div style={s.composerRow}>
          {session.isStreaming ? (
            <button type="button" style={s.stopBtn} onClick={session.stop}>
              Stop
            </button>
          ) : (
            <button
              type="button"
              style={draft.trim() ? s.sendBtn : s.sendBtnDisabled}
              onClick={onSubmit}
              disabled={!draft.trim() || !appId}
            >
              Send
            </button>
          )}
        </div>
      </div>
    </section>
  );
}

function MessageRow({ message }: { message: ChatMessage }) {
  return (
    <div style={message.role === 'user' ? s.userMsg : s.assistantMsg}>
      <div style={s.msgRoleTag}>{message.role}</div>
      {message.text && <div style={s.msgText}>{message.text}</div>}
      {message.tools && message.tools.length > 0 && (
        <div style={s.toolsList}>
          {message.tools.map((t, i) => <ToolCallCard key={i} tool={t} />)}
        </div>
      )}
    </div>
  );
}

function ToolCallCard({ tool }: { tool: ToolCall }) {
  const summary = summarizeArgs(tool.name, tool.args);
  return (
    <div style={s.toolCard}>
      <div style={s.toolHead}>
        <span style={s.toolName}>{tool.name}</span>
        <span style={statusStyle(tool.status)}>{tool.status}</span>
      </div>
      {summary && <div style={s.toolArgs}>{summary}</div>}
    </div>
  );
}

function ConfirmDialog({
  confirm,
  onAnswer,
}: {
  confirm: { tool: string; args: unknown; reason: string } | null;
  onAnswer: (proceed: boolean) => void;
}) {
  if (!confirm) return null;
  return (
    <div style={s.confirmBox}>
      <div style={s.confirmTitle}>Confirm: {confirm.tool}</div>
      <div style={s.confirmReason}>{confirm.reason}</div>
      <div style={s.confirmArgs}>{JSON.stringify(confirm.args, null, 2)}</div>
      <div style={s.confirmActions}>
        <button type="button" style={s.confirmCancelBtn} onClick={() => onAnswer(false)}>
          Cancel
        </button>
        <button type="button" style={s.confirmProceedBtn} onClick={() => onAnswer(true)}>
          Proceed
        </button>
      </div>
    </div>
  );
}

function summarizeArgs(name: string, args: unknown): string | null {
  if (!args || typeof args !== 'object') return null;
  const a = args as Record<string, unknown>;
  switch (name) {
    case 'createView':
      return `${a.type} "${a.name}"`;
    case 'linkChild':
      return `${shortId(a.childId)} → ${shortId(a.parentId)} @ ${a.sort}`;
    case 'updateViewStyle':
      return `${shortId(a.viewId)}`;
    case 'addInteraction':
      return `${shortId(a.viewId)} ${a.trigger}`;
    case 'renameView':
      return `${shortId(a.viewId)} → "${a.name}"`;
    case 'getView':
    case 'getViewTree':
    case 'searchViewsByName':
      return JSON.stringify(args);
    default:
      return null;
  }
}

function shortId(id: unknown): string {
  return typeof id === 'string' ? id.slice(0, 8) : String(id);
}

function statusStyle(status: ToolCall['status']): React.CSSProperties {
  const base: React.CSSProperties = {
    fontSize: 9, fontWeight: 700,
    textTransform: 'uppercase' as const, letterSpacing: 0.5,
    padding: '2px 6px', borderRadius: 3,
  };
  switch (status) {
    case 'pending': return { ...base, color: '#94A3B8', backgroundColor: '#F1F5F9' };
    case 'executing': return { ...base, color: '#4F46E5', backgroundColor: '#EEF2FF' };
    case 'done': return { ...base, color: '#047857', backgroundColor: '#ECFDF5' };
    case 'failed': return { ...base, color: '#B91C1C', backgroundColor: '#FEF2F2' };
  }
}

const s: Record<string, React.CSSProperties> = {
  section: {
    padding: 12, display: 'flex', flexDirection: 'column', gap: 10,
    height: '100%', overflow: 'hidden', minHeight: 0,
  },
  header: { display: 'flex', alignItems: 'baseline', gap: 6 },
  headingText: {
    fontSize: 11, fontWeight: 700, color: '#64748B',
    textTransform: 'uppercase' as const, letterSpacing: 0.5,
  },
  streamingTag: {
    fontSize: 10, fontWeight: 600, color: '#4F46E5',
    backgroundColor: '#EEF2FF', padding: '2px 6px', borderRadius: 8,
  },
  activeModelTag: {
    fontSize: 10, fontWeight: 600, color: '#475569',
    backgroundColor: '#F1F5F9', padding: '2px 6px', borderRadius: 8,
    marginLeft: 'auto',
  },
  messages: {
    flex: 1, overflowY: 'auto', display: 'flex', flexDirection: 'column', gap: 10,
    padding: 6, backgroundColor: '#F8FAFC', borderRadius: 6,
    border: '1px solid #E2E8F0',
  },
  empty: { fontSize: 11, color: '#94A3B8', lineHeight: 1.4, margin: 0 },
  userMsg: {
    padding: '8px 10px', backgroundColor: '#EEF2FF',
    borderRadius: 6, alignSelf: 'flex-end', maxWidth: '85%',
  },
  assistantMsg: {
    padding: '8px 10px', backgroundColor: '#fff',
    borderRadius: 6, alignSelf: 'flex-start', maxWidth: '95%',
    border: '1px solid #E2E8F0',
  },
  msgRoleTag: {
    fontSize: 9, fontWeight: 700, color: '#94A3B8',
    textTransform: 'uppercase' as const, letterSpacing: 0.5, marginBottom: 4,
  },
  msgText: { fontSize: 12, color: '#0F172A', lineHeight: 1.5, whiteSpace: 'pre-wrap' as const },
  toolsList: { marginTop: 6, display: 'flex', flexDirection: 'column', gap: 4 },
  toolCard: {
    padding: 6, fontSize: 11, backgroundColor: '#F1F5F9',
    border: '1px solid #E2E8F0', borderRadius: 4,
  },
  toolHead: { display: 'flex', alignItems: 'center', gap: 6 },
  toolName: { fontFamily: 'ui-monospace, monospace', fontSize: 11, color: '#0F172A', flex: 1 },
  toolArgs: { marginTop: 2, fontSize: 10, color: '#64748B', fontFamily: 'ui-monospace, monospace' },
  error: { fontSize: 11, color: '#B91C1C', margin: 0 },
  confirmBox: {
    padding: 10, backgroundColor: '#FEF3C7', borderRadius: 6,
    border: '1px solid #FCD34D', display: 'flex', flexDirection: 'column', gap: 6,
  },
  confirmTitle: { fontSize: 12, fontWeight: 600, color: '#92400E' },
  confirmReason: { fontSize: 11, color: '#78350F' },
  confirmArgs: {
    fontSize: 10, color: '#78350F', fontFamily: 'ui-monospace, monospace',
    backgroundColor: 'rgba(0,0,0,0.05)', padding: 6, borderRadius: 4,
    whiteSpace: 'pre-wrap' as const, maxHeight: 100, overflowY: 'auto',
  },
  confirmActions: { display: 'flex', justifyContent: 'flex-end', gap: 6 },
  confirmCancelBtn: {
    padding: '4px 10px', fontSize: 11, color: '#92400E',
    backgroundColor: 'transparent', border: '1px solid #FCD34D',
    borderRadius: 4, cursor: 'pointer',
  },
  confirmProceedBtn: {
    padding: '4px 10px', fontSize: 11, color: '#fff',
    backgroundColor: '#92400E', border: 'none', borderRadius: 4, cursor: 'pointer',
  },
  composer: { display: 'flex', flexDirection: 'column', gap: 6 },
  textarea: {
    padding: 8, fontSize: 12, color: '#0F172A',
    border: '1px solid #CBD5E1', borderRadius: 4, fontFamily: 'inherit',
    resize: 'none', boxSizing: 'border-box',
  },
  composerRow: { display: 'flex', justifyContent: 'flex-end' },
  sendBtn: {
    padding: '6px 14px', fontSize: 12, fontWeight: 600,
    color: '#fff', backgroundColor: '#4F46E5',
    border: 'none', borderRadius: 4, cursor: 'pointer',
  },
  sendBtnDisabled: {
    padding: '6px 14px', fontSize: 12, fontWeight: 600,
    color: '#94A3B8', backgroundColor: '#E2E8F0',
    border: 'none', borderRadius: 4, cursor: 'not-allowed',
  },
  stopBtn: {
    padding: '6px 14px', fontSize: 12, fontWeight: 600,
    color: '#B91C1C', backgroundColor: '#fff',
    border: '1px solid #FECACA', borderRadius: 4, cursor: 'pointer',
  },
};
